/**
 * PIR (Priority Intelligence Requirement) payload helpers.
 * Canonical shape matches operational PIR docs:
 *   title, question, supportedDecision, priority, owner, timeframe{start,due},
 *   areaOfInterest, classification, tags, eeis[{id,text}], currentAssessment
 * Status and supportingIIRs are derived at read time (not user-written).
 * Legacy: subject, informationRequirement, expirationDate, eei:{what,where,when,detail}.
 */

import {normalizeEeiPriority} from '../../firebase/firestore/eei';

export const PIR_STATUS = {
    UNCOVERED: 'Uncovered',
    PARTIAL: 'Partially Answered',
    COVERED: 'Covered',
};

/**
 * Coverage overallScore (0–100) → status label.
 * <10 Uncovered, 10–90 Partially Answered, ≥90 Covered; missing → Uncovered.
 */
export function computePirStatus(overallScore) {
    if (overallScore == null || Number.isNaN(Number(overallScore))) {
        return PIR_STATUS.UNCOVERED;
    }
    const score = Number(overallScore);
    if (score < 10) return PIR_STATUS.UNCOVERED;
    if (score >= 90) return PIR_STATUS.COVERED;
    return PIR_STATUS.PARTIAL;
}

/** Canonical EEI display id: prefer unbound eeiNumber, else legacy {PIR}-EEI-{n}. */
export function formatEeiId(pirId, n) {
    const num = Number(n) || 1;
    if (!pirId) return `EEI-${num}`;
    return `${pirId}-EEI-${num}`;
}

/**
 * Resolve EEIs array from table-backed payload (id/eeiNumber/text) or legacy nested eeis.
 * Does not rewrite UUIDs to PIR-scoped ids.
 */
export function resolvePirEeis(pir) {
    if (!pir) return [];
    const pirId = pir.icrNumber || pir.pirId || '';
    let raw = [];
    if (Array.isArray(pir.eeis) && pir.eeis.length > 0) {
        raw = pir.eeis
            .map((e, i) => {
                if (typeof e === 'string') return {id: formatEeiId(pirId, i + 1), text: e};
                const text = e?.text;
                if (!text) return null;
                return {
                    id: e.id || formatEeiId(pirId, i + 1),
                    text: String(text),
                    eeiNumber: e.eeiNumber || e.eei_number || null,
                    status: e.status || null,
                    priority: e.priority || null,
                };
            })
            .filter(Boolean);
        return raw;
    }
    const legacy = pir.eei || {};
    for (const key of ['what', 'where', 'when', 'detail']) {
        const val = legacy[key];
        if (!val || typeof val !== 'string' || !val.trim()) continue;
        if (key === 'what' && val.includes('\n')) {
            raw.push(...parseEeisFromText(val, pirId));
        } else {
            raw.push({id: `legacy-${key}`, text: val.trim()});
        }
    }
    return raw.map((e, i) => ({
        id: e.id || formatEeiId(pirId, i + 1),
        text: e.text,
        eeiNumber: e.eeiNumber || null,
    }));
}

export function resolvePirTitle(pir) {
    if (!pir) return '';
    return pir.title || pir.subject || '';
}

export function resolvePirQuestion(pir) {
    if (!pir) return '';
    return pir.question || pir.informationRequirement || '';
}

export function resolvePirTimeframe(pir) {
    if (!pir) return {start: '', due: ''};
    const tf = pir.timeframe && typeof pir.timeframe === 'object' ? pir.timeframe : {};
    return {
        start: tf.start || '',
        due: tf.due || pir.expirationDate || '',
    };
}

/** ICR's own priority field (Low/Medium/High), not a roll-up from linked EEIs. */
export function resolveIcrPriority(pir) {
    return normalizeEeiPriority(pir?.priority || 'Medium');
}

/** Resolve eeiIds + selectedEeis for ICR create/edit forms from API payload. */
export function resolveIcrFormEeiState(icr) {
    const resolved = resolvePirEeis(icr);
    const byId = new Map(
        resolved.filter((e) => e?.id).map((e) => [String(e.id), e]),
    );
    const fromApi = Array.isArray(icr?.eeiIds)
        ? icr.eeiIds.map(String).filter(Boolean)
        : [];
    const eeiIds = fromApi.length > 0
        ? fromApi
        : resolved
            .map((e) => e?.id)
            .filter((id) => id && !String(id).startsWith('legacy-'));
    const selectedEeis = eeiIds.map((id) => byId.get(id)).filter(Boolean);
    return {eeiIds, selectedEeis};
}

function eeiLinkSnapshot(form = {}) {
    return JSON.stringify({
        eeiIds: (Array.isArray(form.eeiIds) ? form.eeiIds : []).map(String).filter(Boolean).sort(),
        newEeis: (Array.isArray(form.newEeis) ? form.newEeis : []).map(String).filter(Boolean).sort(),
        gapIds: (Array.isArray(form.gapIds) ? form.gapIds : []).map(String).filter(Boolean).sort(),
    });
}

/** True when the user changed linked EEIs, new EEIs, or promoted gaps. */
export function eeiLinkFieldsChanged(beforeForm = {}, afterForm = {}) {
    return eeiLinkSnapshot(beforeForm) !== eeiLinkSnapshot(afterForm);
}

/**
 * Drop EEI link fields from a save payload when the linker section was untouched.
 * Avoids accidental backend sync that can delete linked EEIs on metadata-only edits.
 */
export function omitUnchangedEeiLinkFields(payload, beforeForm, afterForm) {
    if (!payload || eeiLinkFieldsChanged(beforeForm, afterForm)) {
        return payload;
    }
    const next = {...payload};
    delete next.eeiIds;
    delete next.newEeis;
    delete next.gapIds;
    delete next.eeis;
    return next;
}

export function parseEeisFromText(text, pirId = '') {
    if (!text || typeof text !== 'string') return [];
    return text
        .split(/\r?\n/)
        .map((line) => line.trim().replace(/^\d+[.)]\s*/, ''))
        .filter(Boolean)
        .map((t, i) => ({
            id: formatEeiId(pirId, i + 1),
            text: t,
        }));
}

export function eeisToText(eeis) {
    if (!Array.isArray(eeis) || eeis.length === 0) return '';
    return eeis.map((e) => (typeof e === 'string' ? e : e?.text || '')).filter(Boolean).join('\n');
}

/**
 * Build eeis[{id,text}] from form lines, preserving existing ids when possible.
 * @param {string[]} eeisLines
 * @param {Array<{id:string,text:string}>} existingEeis
 * @param {string} [pirId] icrNumber for canonical ids
 */
export function buildEeisFromLines(eeisLines, existingEeis = [], pirId = '') {
    const cleaned = (Array.isArray(eeisLines) ? eeisLines : [])
        .map((t) => String(t ?? '').trim())
        .filter(Boolean);
    const existing = Array.isArray(existingEeis) ? existingEeis : [];
    const usedIds = new Set();

    return cleaned.map((text, i) => {
        const preferred = formatEeiId(pirId, i + 1);
        let id = null;
        const atIndex = existing[i];
        if (atIndex?.id && atIndex.text === text && !usedIds.has(atIndex.id)) {
            if (pirId && /^EEI-\d+$/.test(atIndex.id)) {
                id = preferred;
            } else if (pirId && atIndex.id.startsWith(`${pirId}-EEI-`)) {
                id = atIndex.id;
            } else if (!pirId) {
                id = atIndex.id;
            } else {
                id = preferred;
            }
        }
        if (!id) {
            const byText = existing.find((e) => e.text === text && e.id && !usedIds.has(e.id));
            if (byText) {
                if (pirId && /^EEI-\d+$/.test(byText.id)) id = preferred;
                else if (pirId && byText.id.startsWith(`${pirId}-EEI-`)) id = byText.id;
                else if (!pirId) id = byText.id;
            }
        }
        if (!id) {
            let n = i + 1;
            while (usedIds.has(formatEeiId(pirId, n))) n += 1;
            id = formatEeiId(pirId, n);
        }
        usedIds.add(id);
        return {id, text};
    });
}

/**
 * Machine-linked IIR ids for display (never user-edited on the form).
 * Prefers payload.supportingIIRs; else extracts IIR-typed products from a coverage snapshot.
 */
export function resolveSupportingIirs(pir, coverageSnapshot = null) {
    if (Array.isArray(pir?.supportingIIRs) && pir.supportingIIRs.length > 0) {
        return pir.supportingIIRs.map(String).filter(Boolean);
    }
    if (!coverageSnapshot) return [];
    const ids = new Set();
    const reqs = coverageSnapshot.requirements || coverageSnapshot.coverageByReq || [];
    const list = Array.isArray(reqs) ? reqs : Object.values(reqs || {});
    for (const req of list) {
        const answers = req?.answers || req?.fintelAnswers || [];
        for (const ans of answers) {
            const type = String(ans?.productType || ans?.type || '').toLowerCase();
            if (type === 'iir') {
                const id = ans.reportId || ans.fintelId || ans.productId || ans.id;
                if (id) ids.add(String(id));
            }
        }
    }
    return [...ids];
}

/**
 * Build canonical requirement fields from form state.
 * Prefers first-class eeiIds + newEeis; falls back to legacy eeisLines.
 * @param {object} form
 * @param {{existingEeis?: Array<{id:string,text:string}>, pirId?: string}} [opts]
 */
export function buildPirRequirementFields(form, opts = {}) {
    const question = (form.question || form.informationRequirement || '').trim();
    const pirId = opts.pirId || form.icrNumber || '';
    const existingEeis = opts.existingEeis || resolvePirEeis({...form, icrNumber: pirId});

    const eeiIds = Array.isArray(form.eeiIds)
        ? form.eeiIds.map(String).filter(Boolean)
        : [];
    const newEeis = (Array.isArray(form.newEeis) ? form.newEeis : [])
        .map((t) => String(t ?? '').trim())
        .filter(Boolean)
        .map((text) => ({text}));
    const gapIds = Array.isArray(form.gapIds)
        ? form.gapIds.map(String).filter(Boolean)
        : [];

    if (form.manageEeis) {
        // Dual-write: keep nested eeis snapshot from selected + new text for legacy readers
        const selected = Array.isArray(form.selectedEeis) ? form.selectedEeis : [];
        const eeisFromSelected = selected
            .filter((e) => eeiIds.includes(e.id))
            .map((e) => ({id: e.id, text: e.text, eeiNumber: e.eeiNumber}));
        const pendingGaps = Array.isArray(form.pendingGaps) ? form.pendingGaps : [];
        const eeisFromGaps = pendingGaps
            .filter((g) => gapIds.includes(g.id))
            .map((g) => ({text: g.text, gapId: g.id}));
        const eeis = [...eeisFromSelected, ...newEeis, ...eeisFromGaps];
        return {
            question,
            informationRequirement: question,
            eeiIds,
            newEeis,
            gapIds,
            eeis: eeis.length > 0 ? eeis : undefined,
            eei: undefined,
        };
    }

    let eeis;
    if (Array.isArray(form.eeisLines)) {
        eeis = buildEeisFromLines(form.eeisLines, existingEeis, pirId);
    } else if (Array.isArray(form.eeis) && form.eeis.length > 0) {
        eeis = resolvePirEeis({eeis: form.eeis, icrNumber: pirId});
    } else {
        eeis = parseEeisFromText(form.eeisText || '', pirId);
    }
    return {
        question,
        informationRequirement: question,
        eeis,
        eeiIds: eeis.filter((e) => e.id && !String(e.id).includes('-EEI-') && e.id.length > 20).map((e) => e.id),
        newEeis: eeis
            .filter((e) => !e.id || String(e.id).includes('-EEI-') || e.id.length <= 20)
            .map((e) => ({text: e.text})),
        eei: undefined,
    };
}

function toIsoOrNull(value) {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toISOString();
}

/**
 * Full PIR write payload from create/edit form state.
 */
export function buildPirSavePayload(form, opts = {}) {
    const title = (form.title || form.subject || '').trim() || 'Untitled PIR';
    const pirId = opts.pirId || form.icrNumber || '';
    const req = buildPirRequirementFields(form, {...opts, pirId});
    const start = form.timeframeStart || form.timeframe?.start || '';
    const due = form.timeframeDue || form.timeframe?.due || form.expirationDate || '';
    const dueIso = toIsoOrNull(due);
    const startIso = toIsoOrNull(start);

    return {
        title,
        subject: title,
        ...req,
        supportedDecision: (form.supportedDecision || '').trim(),
        priority: normalizeEeiPriority(form.priority || 'Medium'),
        owner: (form.owner || '').trim(),
        timeframe: {
            start: startIso,
            due: dueIso,
        },
        expirationDate: dueIso,
        areaOfInterest: (form.areaOfInterest || '').trim(),
        classification: form.classification || 'UNCLASSIFIED',
        tags: Array.isArray(form.tags) ? form.tags : [],
        targets: Array.isArray(form.targets)
            ? form.targets.map((t) => (typeof t === 'string' ? t : t?.entityRef)).filter(Boolean)
            : [],
        currentAssessment: (form.currentAssessment || '').trim(),
        backgroundJustification: undefined,
    };
}
